import { useMutation } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import type {
  ShiftListParams,
  CreateShiftRequest,
  UpdateShiftRequest,
  CompleteShiftRequest,
  RowVersion,
  ShiftDto,
} from '@signalvote/shared';
import { defineQuery, defineWrite, requireSelectedCampaign } from '@/lib/data';

const api = getApiClient();

/**
 * Shifts, optionally filtered. A shift write's `X-SV-Writes` names `Shift`, which this read
 * declares, so no mutation below lists a key to refresh.
 */
export const useShifts = defineQuery({
  key: (params: ShiftListParams | undefined) => ['shifts', 'list', params],
  reads: 'GET /api/Shifts',
  fetch: (params, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.shifts.getShifts(params);
  },
  staleTime: 30_000,
});

export const useShift = defineQuery({
  key: (id: string) => ['shifts', 'detail', id],
  reads: 'GET /api/Shifts/{id}',
  fetch: (id, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.shifts.getShift(id);
  },
  staleTime: 30_000,
});

export function useCreateShift() {
  return useMutation({ mutationFn: (data: CreateShiftRequest) => api.shifts.createShift(data) });
}

export type ShiftEdit = Omit<UpdateShiftRequest, 'concurrencyVersion'>;

/** Edit a shift. Pass the row as read; the write sends its freshest token, and a 409 is a stale write. */
export const useUpdateShift = defineWrite({
  row: 'Shift',
  send: (id, concurrencyVersion: RowVersion, edit: ShiftEdit): Promise<ShiftDto> =>
    api.shifts.updateShift(id, { ...edit, concurrencyVersion }),
});

export function useDeleteShift() {
  return useMutation({ mutationFn: (id: string) => api.shifts.deleteShift(id) });
}

export function useClaimShift() {
  return useMutation({ mutationFn: (id: string) => api.shifts.claimShift(id) });
}

/** Release a claimed shift (before starting). */
export function useReleaseShift() {
  return useMutation({ mutationFn: (id: string) => api.shifts.releaseShift(id) });
}

export function useCompleteShift() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompleteShiftRequest }) => api.shifts.completeShift(id, data),
  });
}
